import { MapsAPILoader } from '@agm/core';
import { AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Subject, of } from 'rxjs';
import { catchError, filter, switchMap, takeUntil } from 'rxjs/operators';

import { AddressField, getAddressField } from 'src/lib/lib-ngx/utils/AddressField';
import { ApiPublicService } from 'src/lib/lib-ngx/web-services/api-public.service';
import { IAddressDataContent, IPickup } from 'src/lib/lib-shared/api/All.Type';
import { IHouseDetailsList } from 'src/lib/lib-shared/models/House';

@Component({
  selector: 'lm-pickup-select-modal',
  templateUrl: './pickup-select-modal.component.html',
  styleUrls: ['./pickup-select-modal.component.scss'],
})
export class PickupSelectModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() house: IHouseDetailsList;

  @ViewChild('searchAddressRef') public searchAddressRef: ElementRef;

  public pickupList: IPickup[] = [];
  public selectedPickup: IPickup | null = null;
  public searchAddress = '';

  private searchCoords$ = new BehaviorSubject<IAddressDataContent | null>(null);
  private destroy$ = new Subject<void>();

  constructor(
    public activeModal: NgbActiveModal,
    private apiPublicService: ApiPublicService,
    private mapsAPILoader: MapsAPILoader,
    private ngZone: NgZone,
  ) {}

  ngOnInit() {
    const addr = this.house.shippingAddress;
    this.searchAddress = addr.formattedAddress || '';
    
    this.searchCoords$.pipe(
      filter(coords => !!coords),
      takeUntil(this.destroy$),
      switchMap(coords => this.apiPublicService.getPickup(coords).pipe(
        catchError(() => {
          return of([] as IPickup[]);
        })
      )),
    ).subscribe(pickups => {
      this.pickupList = pickups;
      if (this.house.shippingAddress.pickupId && !this.selectedPickup) {
        this.selectedPickup = this.pickupList.find(p => p.id === this.house.shippingAddress.pickupId) || null;
      }
    });

    this.searchCoords$.next({
      streetNumber: addr.streetNumber,
      route: addr.route,
      postalCode: addr.postalCode,
      locality: addr.locality,
      country: addr.country,
      formattedAddress: addr.formattedAddress,
      lat: addr.lat,
      lng: addr.lng,
    });
  }

  ngAfterViewInit() {
    this.mapsAPILoader.load().then(() => {
      const autocomplete = new google.maps.places.Autocomplete(this.searchAddressRef.nativeElement, {
        types: ['address'],
        componentRestrictions: { country: 'fr' },
      });
      autocomplete.addListener('place_changed', () => {
        this.ngZone.run(() => {
          const place = autocomplete.getPlace();
          if (!place.geometry) { return; }
          const postalCode = getAddressField(place, AddressField.POSTALCODE);
          const route = getAddressField(place, AddressField.ROUTE);
          if (!postalCode || !route) { return; }
          const newCoords = {
            streetNumber: getAddressField(place, AddressField.STREETNUMBER),
            route: route,
            postalCode: postalCode,
            locality: getAddressField(place, AddressField.LOCALITY),
            country: getAddressField(place, AddressField.COUNTRY),
            formattedAddress: place.formatted_address || '',
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          };
          
          this.searchCoords$.next(newCoords);
        });
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectPickup(pickup: IPickup) {
    this.selectedPickup = pickup;
  }

  onConfirm() {
    this.activeModal.close(this.selectedPickup);
  }

  onCancel() {
    this.activeModal.dismiss();
  }

  formatRange(metres: number): string {
    return metres >= 1000
      ? (metres / 1000).toFixed(1) + ' km'
      : metres + ' m';
  }
}
